import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { I18nService } from 'i18n';
import { FoundingMemberBadgeComponent } from 'shared';
import { CommunityService } from '../../../services/community.service';
import { CommunityPublicProfile, profileDisplayName, socialUrl } from '../../../models/community-profile.models';
import { communityErrorKey } from '../../../models/community.models';
import { CommunityAvatarComponent } from '../../../components/community-avatar/community-avatar.component';
import { CommunityBadgesComponent } from '../../../components/community-badges/community-badges.component';

/**
 * Another member's public profile (`community/u/:userId`, §6): what they chose
 * to show — the backend has already applied their per-field visibility, so
 * every contact field that arrives is meant to be seen. Offers "send message"
 * (when the communication rules allow it) and a personal block.
 */
@Component({
  selector: 'app-community-member-page',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslocoModule, FoundingMemberBadgeComponent, CommunityAvatarComponent, CommunityBadgesComponent],
  templateUrl: './community-member-page.component.html',
  styleUrl: './community-member-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityMemberPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private community = inject(CommunityService);
  private i18n = inject(I18nService);

  profile = signal<CommunityPublicProfile | null>(null);
  loading = signal(true);
  /** Translation key of a blocking error (404, `community_blocked`). */
  error = signal<string | null>(null);
  busy = signal(false);
  actionError = signal<string | null>(null);

  userId = signal<number | null>(null);
  displayName = computed(() => profileDisplayName(this.profile()?.user));
  /** Social entries as [network, url] pairs; only present when the member opened them. */
  socialLinks = computed<{ key: string; url: string; label: string }[]>(() => {
    const social = this.profile()?.contact.social;
    if (!social) return [];
    return Object.entries(social)
      .filter(([, v]) => !!v)
      .map(([key, value]) => ({ key, url: socialUrl(key, value), label: value }));
  });
  hasContact = computed(() => {
    const c = this.profile()?.contact;
    return !!c && (!!c.email || !!c.phone || !!c.whatsapp || !!c.website || !!c.city || this.socialLinks().length > 0);
  });
  /** "Country · Region" as the member chose to show it (either may be private). */
  location = computed(() => {
    const c = this.profile()?.contact;
    return [c?.country, c?.region].filter(Boolean).join(' · ');
  });
  hasAbout = computed(() => {
    const a = this.profile()?.about;
    return !!a && ((a.exchange_topics?.length ?? 0) > 0 || (a.interests?.length ?? 0) > 0 || (a.hobbies?.length ?? 0) > 0);
  });
  /** The professional block in the backend's key order, empty values dropped. */
  detailEntries = computed<{ key: string; value: string }[]>(() => {
    const details = this.profile()?.practitioner?.details ?? {};
    return Object.entries(details).filter(([, v]) => !!v).map(([key, value]) => ({ key, value }));
  });

  private routeSub?: Subscription;

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = Number(params.get('userId'));
      this.userId.set(id && !Number.isNaN(id) ? id : null);
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  profileSettingsLink(): string {
    return `/${this.lang}/profile`;
  }

  composeLink(): string {
    return `/${this.lang}/community/messages/new`;
  }

  /** WhatsApp deep link: digits only, as wa.me wants them. */
  whatsappLink(value: string): string {
    const digits = value.replace(/[^\d]/g, '');
    return digits ? `https://wa.me/${digits}` : '';
  }

  load(): void {
    const id = this.userId();
    if (!id) {
      this.loading.set(false);
      this.error.set('community.member.not_found');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.community.getMemberProfile(id).subscribe({
      next: res => {
        this.profile.set(res);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.profile.set(null);
        this.error.set(err?.status === 404 ? 'community.member.not_found' : communityErrorKey(err));
      }
    });
  }

  message(): void {
    const id = this.userId();
    if (id) this.router.navigate([this.composeLink()], { queryParams: { to: id } });
  }

  toggleBlock(): void {
    const p = this.profile();
    const id = this.userId();
    if (!p || !id) return;
    this.busy.set(true);
    this.actionError.set(null);
    const request = p.is_blocked ? this.community.unblockUser(id) : this.community.blockUser(id);
    request.subscribe({
      next: res => {
        this.busy.set(false);
        // Blocking hides the profile from now on (403 on reload), so keep the
        // page as it is and only flip the flag the backend confirmed.
        this.profile.update(current => (current ? { ...current, is_blocked: res.is_blocked, can_message: res.is_blocked ? false : current.can_message } : current));
        if (!res.is_blocked) this.load();
      },
      error: err => {
        this.busy.set(false);
        this.actionError.set(communityErrorKey(err));
      }
    });
  }
}
